'use client';

import React, { useCallback, useState } from 'react';

export type ProductPriceRangeSectionProps = {
  title: string;
  min: number;
  max: number;
  selectedMin: number;
  selectedMax: number;
  onChange: (min: number, max: number) => void;
  className?: string;
  currency?: string;
};

const ProductPriceRangeSection: React.FC<ProductPriceRangeSectionProps> = ({ title, min, max, selectedMin, selectedMax, onChange, className = '', currency = '$' }) => {
  const [localMin, setLocalMin] = useState(selectedMin);
  const [localMax, setLocalMax] = useState(selectedMax);

  const handleMinChange = useCallback(
    (value: number) => {
      const newMin = Math.min(value, localMax);
      setLocalMin(newMin);
      onChange(newMin, localMax);
    },
    [localMax, onChange],
  );

  const handleMaxChange = useCallback(
    (value: number) => {
      const newMax = Math.max(value, localMin);
      setLocalMax(newMax);
      onChange(localMin, newMax);
    },
    [localMin, onChange],
  );

  const formatProductPrice = (ProductPrice: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(ProductPrice);
  };

  const percentage = (value: number) => ((value - min) / (max - min)) * 100;

  return (
    <div className={`${className} w-full overflow-hidden`}>
      <h3 className="font-semibold text-gray-900 mb-3">{title}</h3>

      {/* ProductPrice Range Display */}
      <div className="mb-4">
        <div className="flex justify-between items-center text-sm text-gray-700">
          <span>
            {currency}
            {formatProductPrice(localMin)}
          </span>
          <span>
            {currency}
            {formatProductPrice(localMax)}
          </span>
        </div>
      </div>

      {/* Custom Range Slider */}
      <div className="relative mb-6 px-1">
        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
          {/* Selected Range */}
          <div
            className="absolute h-2 bg-blue-600 rounded-full"
            style={{
              left: `${percentage(localMin)}%`,
              width: `${percentage(localMax) - percentage(localMin)}%`,
            }}
          />
        </div>

        {/* Min Range Input */}
        <input
          type="range"
          min={min}
          max={max}
          value={localMin}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          className="absolute inset-x-0 top-0 h-2 opacity-0 cursor-pointer appearance-none"
        />

        {/* Max Range Input */}
        <input
          type="range"
          min={min}
          max={max}
          value={localMax}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          className="absolute inset-x-0 top-0 h-2 opacity-0 cursor-pointer appearance-none"
        />
      </div>

      {/* Manual Input Fields */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="min-ProductPrice" className="block text-xs text-gray-600 mb-1">
            Mínimo
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">{currency}</span>
            <input
              id="min-ProductPrice"
              type="number"
              value={localMin}
              onChange={(e) => handleMinChange(Number(e.target.value) || min)}
              min={min}
              max={max}
              className="w-full pl-6 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label htmlFor="max-ProductPrice" className="block text-xs text-gray-600 mb-1">
            Máximo
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">{currency}</span>
            <input
              id="max-ProductPrice"
              type="number"
              value={localMax}
              onChange={(e) => handleMaxChange(Number(e.target.value) || max)}
              min={min}
              max={max}
              className="w-full pl-6 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPriceRangeSection;
